import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Hospital, LogOut,
  ChevronLeft, ChevronRight, Search, Bell, Menu, ShieldCheck, Bug,
  CreditCard, ArrowUpCircle, LifeBuoy, Activity,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAuthStore } from '@/store/auth.store';
import { authApi } from '@/lib/api';
import AppLogo from '@/components/AppLogo';

const NAV_SECTIONS = [
  {
    title: 'PLATFORM',
    items: [
      { to: '/admin',           icon: LayoutDashboard, label: 'Dashboard',  end: true },
      { to: '/admin/hospitals', icon: Hospital,         label: 'Hospitals',  end: false },
    ],
  },
  {
    title: 'BILLING',
    items: [
      { to: '/admin/plans',            icon: CreditCard,    label: 'Plans',            end: false },
      { to: '/admin/upgrade-requests', icon: ArrowUpCircle, label: 'Upgrade Requests', end: false },
    ],
  },
  {
    title: 'SUPPORT',
    items: [
      { to: '/admin/support', icon: LifeBuoy, label: 'Support Tickets', end: false },
    ],
  },
  {
    title: 'SECURITY',
    items: [
      { to: '/admin/rbac',        icon: ShieldCheck, label: 'RBAC Matrix',   end: false },
      { to: '/admin/debug/auth',  icon: Bug,         label: 'Auth Debugger', end: false },
    ],
  },
  {
    title: 'MONITORING',
    items: [
      { to: '/admin/sys-logs', icon: Activity, label: 'System Logs', end: false },
    ],
  },
];

function initials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
}

export default function AdminLayout() {
  const { user, logout } = useAuthStore();
  const navigate          = useNavigate();
  const [collapsed, setCollapsed]   = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  async function handleLogout() {
    try { await authApi.logout(); } catch {}
    logout();
    navigate('/login');
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50">

      {/* ── Top header bar ────────────────────────────────────── */}
      <header className="h-11 bg-navy border-b border-white/10 flex items-center px-4 flex-shrink-0 z-20">
        {/* Mobile hamburger */}
        <button
          className="lg:hidden mr-3 text-white/50 hover:text-white"
          onClick={() => setMobileSidebarOpen(o => !o)}>
          <Menu size={18}/>
        </button>

        {/* Brand */}
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <div className="w-6 h-6 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
            <AppLogo size={15} inverted/>
          </div>
          <span className="text-white font-semibold text-sm leading-tight flex-shrink-0">
            BBS Admin
          </span>
          <span className="text-white/20 flex-shrink-0 hidden sm:block">|</span>
          <span className="text-white/40 text-xs flex-shrink-0 hidden sm:block">
            Platform Console
          </span>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <button className="relative text-white/40 hover:text-white transition-colors">
            <Bell size={16}/>
          </button>

          <div className="h-4 w-px bg-white/15"/>

          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-orange/20 flex items-center justify-center
                            text-orange text-2xs font-bold select-none">
              {initials(user?.name ?? 'BS')}
            </div>
            <span className="text-white/60 text-xs hidden lg:block truncate max-w-[120px]">
              {user?.name}
            </span>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-white/45 hover:text-white text-xs
                       px-2 py-1 rounded hover:bg-white/10 transition-colors">
            <LogOut size={13}/>
            <span className="hidden sm:block">Sign out</span>
          </button>
        </div>
      </header>

      {/* ── Body ─────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Mobile overlay */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-10 lg:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* ── Sidebar ─────────────────────────────────────────── */}
        <aside className={clsx(
          'flex flex-col bg-[#1b2537] border-r border-white/5 transition-all duration-200 flex-shrink-0 z-10',
          /* Desktop collapse */
          'hidden lg:flex',
          collapsed ? 'lg:w-14' : 'lg:w-52',
          /* Mobile: off-canvas slide */
          mobileSidebarOpen && '!flex fixed inset-y-0 left-0 w-52 top-11',
        )}>

          {/* Search */}
          <div className={clsx(
            'border-b border-white/5',
            collapsed ? 'flex justify-center py-3 px-2' : 'p-3',
          )}>
            {collapsed ? (
              <Search size={14} className="text-white/30"/>
            ) : (
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md
                              bg-white/5 border border-white/8 hover:border-white/15 transition-colors">
                <Search size={12} className="text-white/30 flex-shrink-0"/>
                <input
                  placeholder="Filter menu..."
                  className="bg-transparent text-white/60 text-xs outline-none w-full placeholder:text-white/25"
                  readOnly
                />
              </div>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto py-2 space-y-0.5">
            {NAV_SECTIONS.map(section => (
              <div key={section.title}>
                {!collapsed && (
                  <div className="px-3 pt-3 pb-1 text-2xs font-semibold text-white/25 tracking-widest uppercase select-none">
                    {section.title}
                  </div>
                )}
                {section.items.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileSidebarOpen(false)}
                    title={collapsed ? item.label : undefined}
                    className={({ isActive }) => clsx(
                      'flex items-center gap-2.5 mx-2 px-2.5 py-2 rounded-md text-sm transition-colors',
                      collapsed && 'justify-center mx-1 px-0',
                      isActive
                        ? 'bg-orange/15 text-orange font-medium'
                        : 'text-white/50 hover:text-white hover:bg-white/6'
                    )}>
                    <item.icon size={15} className="flex-shrink-0"/>
                    {!collapsed && <span className="text-[13px]">{item.label}</span>}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          {/* Collapse toggle (desktop only) */}
          <div className="border-t border-white/5 p-2">
            <button
              onClick={() => setCollapsed(c => !c)}
              className="flex items-center justify-center w-full py-1.5 rounded text-white/25
                         hover:text-white/60 hover:bg-white/5 transition-colors">
              {collapsed ? <ChevronRight size={13}/> : <ChevronLeft size={13}/>}
            </button>
          </div>
        </aside>

        {/* ── Main content ─────────────────────────────────────── */}
        <main className="flex-1 overflow-auto">
          <Outlet/>
        </main>
      </div>
    </div>
  );
}
